import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { ApiError, type AccountApi } from "@hostdock/sdk";
import { PASSWORD_MIN_LENGTH } from "@hostdock/validation";
import { Button } from "../button";
import { FormError } from "../feedback";
import { Field, Input } from "../form";

export function ChangePasswordForm<TSession>({ api, onChanged, submitLabel = "Change password" }: { api: AccountApi<TSession>; onChanged(session: TSession): void; submitLabel?: string }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const change = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: (session) => {
      setCurrent("");
      setNext("");
      setConfirm("");
      onChanged(session);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (next.length < PASSWORD_MIN_LENGTH) return setLocalError(`Use at least ${PASSWORD_MIN_LENGTH} characters`);
    if (next !== confirm) return setLocalError("Passwords do not match");
    setLocalError(null);
    change.mutate();
  };
  const fe = change.error instanceof ApiError ? change.error.fieldErrors : {};
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <Field label="Current password" error={change.error instanceof ApiError && change.error.code === "CURRENT_PASSWORD_INVALID" ? change.error.message : fe.currentPassword}>
        {(p) => <Input {...p} type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />}
      </Field>
      <Field label="New password" hint={`At least ${PASSWORD_MIN_LENGTH} characters. A passphrase of several words works well.`} error={localError ?? fe.newPassword}>
        {(p) => <Input {...p} type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />}
      </Field>
      <Field label="Confirm new password">
        {(p) => <Input {...p} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />}
      </Field>
      {change.error instanceof ApiError && change.error.code !== "CURRENT_PASSWORD_INVALID" && !Object.keys(fe).length ? <FormError error={change.error} /> : null}
      <div>
        <Button type="submit" variant="primary" loading={change.isPending} disabled={!current || !next || !confirm}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
